const _displayNames = (() => {
  try {
    return new Intl.DisplayNames(["es"], { type: "region" });
  } catch {
    return null;
  }
})();

export function getCountryName(code: string): string {
  if (!code) return code;
  try {
    return _displayNames?.of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

export interface ProfileCompletionFields {
  dateOfBirth?: string | null;
  gender?: string | null;
  country?: string | null;
  city?: string | null;
  yearsPlaying?: number | null;
  playingHand?: string | null;
  playerType?: string | null;
  homeClubId?: string | null;
  homeClubName?: string | null;
}

export interface ProfileCompletionInput {
  name: string;
  profileImageUrl?: string | null;
  profile?: ProfileCompletionFields | null;
}

export interface ProfileCompletionField {
  key: string;
  label: string;
  weight: number;
  filled: boolean;
}

export function getProfileCompletionFields(
  input: ProfileCompletionInput,
): ProfileCompletionField[] {
  const { name, profileImageUrl, profile } = input;
  return [
    { key: "profileImageUrl", label: "Foto de perfil", weight: 15, filled: !!profileImageUrl },
    {
      key: "fullName",
      label: "Nombre completo",
      weight: 10,
      filled: name.includes(" ") && name !== "Jugador",
    },
    { key: "dateOfBirth", label: "Fecha de nacimiento", weight: 12, filled: !!profile?.dateOfBirth },
    { key: "gender", label: "Género", weight: 10, filled: !!profile?.gender },
    { key: "country", label: "País", weight: 10, filled: !!profile?.country },
    { key: "city", label: "Ciudad", weight: 10, filled: !!profile?.city },
    {
      key: "homeClub",
      label: "Club donde juegas",
      weight: 10,
      filled: !!(profile?.homeClubId || profile?.homeClubName),
    },
    { key: "yearsPlaying", label: "Años jugando", weight: 8, filled: (profile?.yearsPlaying ?? 0) > 0 },
    { key: "playingHand", label: "Mano dominante", weight: 5, filled: !!profile?.playingHand },
    { key: "playerType", label: "Estilo de juego", weight: 10, filled: !!profile?.playerType },
  ];
}

export function calculateProfileCompletion(input: ProfileCompletionInput): number {
  const fields = getProfileCompletionFields(input);
  const total = fields.reduce((s, f) => s + f.weight, 0);
  const filled = fields.reduce((s, f) => s + (f.filled ? f.weight : 0), 0);
  if (total === 0) return 0;
  return Math.round((filled / total) * 100);
}

export function isHomeClubMissing(input: ProfileCompletionInput): boolean {
  return !(input.profile?.homeClubId || input.profile?.homeClubName);
}

/**
 * Shared "Home de arranque" (task #495) helpers. Web and mobile both need to
 * decide identically whether a user is in the zero-matches state and, if so,
 * which of the 3 onboarding cases (A/B/C) to show. Keeping this logic here
 * (instead of duplicated per-surface) is what keeps the two Home screens in
 * sync as the product evolves.
 */

export interface StartHomeMatchParticipant {
  team1Player1Id: string;
  team1Player2Id?: string | null;
  team2Player1Id: string;
  team2Player2Id?: string | null;
}

/** True when `userId` is one of the 4 players in `match`. */
export function isMatchParticipant(
  match: StartHomeMatchParticipant,
  userId?: string | null,
): boolean {
  if (!userId) return false;
  return (
    match.team1Player1Id === userId ||
    match.team1Player2Id === userId ||
    match.team2Player1Id === userId ||
    match.team2Player2Id === userId
  );
}

/**
 * `partidosJugados == 0` per task #495: counts approved matches (already
 * recorded in match history) OR pending matches the user participates in
 * (as reporter or opponent) — as soon as either exists, the user is out of
 * the "Home de arranque" state.
 */
export function hasZeroMatchesPlayed(params: {
  approvedMatchesCount: number;
  pendingMatches: StartHomeMatchParticipant[];
  userId?: string | null;
}): boolean {
  if (params.approvedMatchesCount > 0) return false;
  return !params.pendingMatches.some((m) => isMatchParticipant(m, params.userId));
}

export interface StartHomeLeagueLike {
  id: string;
  name: string;
  memberCount: number;
}

/**
 * A = only in the (virtual) National League, no other leagues.
 * B = belongs to >=1 non-national league that already has other members.
 * C = only has non-national league(s) where they're still the sole member
 *     (typically the organizer waiting for others to join).
 */
export type StartHomeCase = "A" | "B" | "C";

export function getStartHomeCase(
  nonNationalLeagues: StartHomeLeagueLike[],
): StartHomeCase {
  if (nonNationalLeagues.length === 0) return "A";
  return nonNationalLeagues.some((l) => l.memberCount > 1) ? "B" : "C";
}

/** Picks the league to reference by name in the case B/C main step. */
export function pickStartHomeLeague(
  nonNationalLeagues: StartHomeLeagueLike[],
  startHomeCase: StartHomeCase,
): StartHomeLeagueLike | null {
  if (nonNationalLeagues.length === 0) return null;
  if (startHomeCase === "B") {
    return nonNationalLeagues.find((l) => l.memberCount > 1) ?? nonNationalLeagues[0];
  }
  return nonNationalLeagues.find((l) => l.memberCount <= 1) ?? nonNationalLeagues[0];
}

export interface DefaultLeagueLike {
  id: string;
  isFavorite: boolean;
}

/**
 * Task #496 — "Registrar Partido" league auto-selection fallback.
 *
 * `leagues` must already be the user's real (non-virtual) league
 * memberships as returned by `GET /api/leagues` (the National League
 * is virtual and never appears there, so callers don't need to
 * filter it out separately).
 *
 * This is only a *lower-priority fallback*: callers must apply it
 * only when no higher-priority context already picked a league (a
 * `leagueId` deep link, or a scheduled match's league).
 *
 * Rules, in order:
 * 1. Exactly one league marked favorite -> that league.
 * 2. Otherwise (zero, or more than one favorite) but exactly one
 *    league total -> that league.
 * 3. Anything else -> null (no auto-selection).
 */
export function getDefaultLeagueId(leagues: DefaultLeagueLike[]): string | null {
  const favorites = leagues.filter((l) => l.isFavorite);
  if (favorites.length === 1) return favorites[0].id;
  if (leagues.length === 1) return leagues[0].id;
  return null;
}
